import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, StickyNote, Trash2, Edit2, Check, X, Mic, FileAudio, FileText, AlarmClock, BookOpen } from "lucide-react";
import { toast } from "sonner";
import AudioRecordModal from "@/pages/trade/AudioRecordModal";

const NotesPage = () => {
  const { API, authHeader, token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [reminderAt, setReminderAt] = useState("");
  const [editReminderAt, setEditReminderAt] = useState("");
  const [recordOpen, setRecordOpen] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  const [transcribingId, setTranscribingId] = useState(null);

  const toDateTimeLocal = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const fromDateTimeLocal = (value) => value ? new Date(value).toISOString() : "";
  const isReminderDue = (note) => note.reminder_at && !note.reminder_done && new Date(note.reminder_at) <= new Date();

  const [voiceLang, setVoiceLang] = useState("auto");
  const [verseTopic, setVerseTopic] = useState("love");
  const [verseTopics, setVerseTopics] = useState([]);

  // Voice note flow: record first, save the audio note, then convert it to text when ready.

  const notifyRemindersChanged = () => window.dispatchEvent(new Event("trade-reminders-changed"));

  const fetchVerseTopic = () => {
    axios.get(`${API}/trade/verse-topic`, { headers: authHeader() })
      .then(res => { setVerseTopic(res.data.topic); setVerseTopics(res.data.topics || []); })
      .catch(e => console.error(e));
  };

  const changeVerseTopic = async (topic) => {
    try {
      const res = await axios.put(`${API}/trade/verse-topic`, { topic }, { headers: authHeader() });
      setVerseTopic(res.data.topic);
      toast.success("Verse topic updated");
    } catch {
      toast.error("Could not save topic");
    }
  };

  const handleVoiceNoteSave = async (file, name) => {
    setSavingVoice(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name || file.name || "Voice note");
      const res = await axios.post(`${API}/trade/notes/audio`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
      });
      setNotes(prev => [res.data, ...prev]);
      setRecordOpen(false);
      toast.success("Voice note saved — tap Convert to Text when ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the recording");
    } finally {
      setSavingVoice(false);
    }
  };

  // Optional immediate conversion from the recorder, without saving the audio.
  const handleVoiceToText = async (file) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("lang", voiceLang);
      const res = await axios.post(`${API}/trade/transcribe`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
      });
      const text = res.data.text || "";
      setNewNote(prev => (prev.trim() ? prev.trim() + "\n" + text : text));
      setRecordOpen(false);
      toast.success("Converted to text — review and tap Save Note");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not convert the recording");
    } finally {
      setTranscribing(false);
    }
  };

  const handleNoteTranscribe = async (note) => {
    setTranscribingId(note.id);
    try {
      const res = await axios.post(`${API}/trade/notes/${note.id}/transcribe?lang=${voiceLang}`, {}, { headers: authHeader() });
      setNotes(prev => prev.map(n => n.id === note.id ? res.data.note : n));
      toast.success(res.data.cached ? "Text is on the note" : "Converted to text");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not convert this voice note");
    } finally {
      setTranscribingId(null);
    }
  };

  const fetchNotes = () => {
    axios.get(`${API}/trade/notes`, { headers: authHeader() })
      .then(res => setNotes(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotes();
    fetchVerseTopic();
    window.addEventListener("trade-reminders-changed", fetchNotes);
    return () => window.removeEventListener("trade-reminders-changed", fetchNotes);
  }, []);

  const addNote = async () => {
    if (!newNote.trim()) {
      toast.error("Write something first");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/trade/notes`, {
        text: newNote.trim(),
        reminder_at: fromDateTimeLocal(reminderAt)
      }, { headers: authHeader() });
      setNotes(prev => [res.data, ...prev]);
      setNewNote("");
      setReminderAt("");
      notifyRemindersChanged();
      toast.success(reminderAt ? "Note and reminder saved" : "Note saved");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.text);
    setEditReminderAt(toDateTimeLocal(note.reminder_at));
  };

  const saveEdit = async (noteId) => {
    if (!editText.trim()) return;
    try {
      const reminder_at = fromDateTimeLocal(editReminderAt);
      await axios.put(`${API}/trade/notes/${noteId}`, { text: editText.trim(), reminder_at }, { headers: authHeader() });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, text: editText.trim(), reminder_at, reminder_done: n.reminder_at === reminder_at ? n.reminder_done : false } : n));
      setEditingId(null);
      setEditText("");
      setEditReminderAt("");
      notifyRemindersChanged();
      toast.success("Note updated");
    } catch {
      toast.error("Failed to update note");
    }
  };

  const markReminderDone = async (noteId) => {
    try {
      await axios.patch(`${API}/trade/notes/${noteId}/reminder`, { done: true }, { headers: authHeader() });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, reminder_done: true } : n));
      notifyRemindersChanged();
      toast.success("Reminder marked done");
    } catch {
      toast.error("Could not update reminder");
    }
  };

  const markVerseRead = async (noteId) => {
    try {
      await axios.post(`${API}/trade/notes/${noteId}/read-verse`, {}, { headers: authHeader() });
      setNotes(prev => prev.filter(n => n.id !== noteId));
      notifyRemindersChanged();
      toast.success("Amen — verse read");
    } catch {
      toast.error("Could not remove verse");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/trade/notes/${noteId}`, { headers: authHeader() });
      setNotes(prev => prev.filter(n => n.id !== noteId));
      notifyRemindersChanged();
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Notepad</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Notes</h1>
          <p className="text-white/50 mt-2">Quick notes for your business — saved instantly</p>
        </motion.div>

        {/* Bible verse topic picker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="trade-card rounded-sm p-5 mb-8"
          data-testid="verse-topic-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <BookOpen size={18} className="trade-gold" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest trade-gold">Bible Verses · Every 3 Hours</p>
              <p className="text-sm text-white/70">Choose a theme — new verses arrive as reminders until you read them</p>
            </div>
          </div>
          <Select value={verseTopic} onValueChange={changeVerseTopic}>
            <SelectTrigger className="trade-input rounded-sm h-11 w-full sm:w-56 mt-4" data-testid="verse-topic-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#142039] border-white/10">
              {verseTopics.map(t => (
                <SelectItem key={t.key} value={t.key} data-testid={`verse-topic-${t.key}`}>
                  {t.label} ({t.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Composer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
        >
          <Textarea
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            rows={3}
            placeholder="Write a note — phone numbers, prices, reminders, anything..."
            className="trade-input rounded-sm mb-4"
            data-testid="note-composer-input"
          />
          <div className="mb-4 max-w-sm">
            <Label className="text-xs text-white/60">Reminder date/time (optional)</Label>
            <Input
              type="datetime-local"
              value={reminderAt}
              onChange={e => setReminderAt(e.target.value)}
              className="trade-input mt-1 h-11 rounded-sm"
              data-testid="note-reminder-input"
            />
            <p className="text-[11px] text-white/35 mt-1">It will keep showing inside Trade until you mark it done.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={addNote}
              disabled={saving}
              className="trade-btn-primary rounded-sm h-11 px-6"
              data-testid="save-note-btn"
            >
              <Plus size={16} className="mr-2" />
              {saving ? "Saving..." : "Save Note"}
            </Button>
            <Button
              onClick={() => setRecordOpen(true)}
              disabled={transcribing || savingVoice}
              className="bg-white/10 text-white hover:bg-white/20 rounded-sm h-11 px-6"
              data-testid="voice-note-btn"
            >
              <Mic size={16} className="mr-2" />
              {savingVoice ? "Saving audio..." : transcribing ? "Writing text..." : "Record Audio"}
            </Button>
            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-white/50 whitespace-nowrap">Speech</Label>
              <Select value={voiceLang} onValueChange={setVoiceLang}>
                <SelectTrigger className="trade-input h-9 rounded-sm w-32 text-xs" data-testid="voice-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10">
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="am">አማርኛ Amharic</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="mt-3 text-xs text-white/40" data-testid="voice-note-help">
            Record audio first, then save it as a voice note or convert it to text.
          </p>
        </motion.div>

        {recordOpen && (
          <AudioRecordModal
            onClose={() => setRecordOpen(false)}
            onSave={handleVoiceNoteSave}
            onConvert={handleVoiceToText}
            saveLabel="Save Voice Note"
            convertLabel="Convert to Text"
          />
        )}

        {/* Notes grid */}
        {loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : notes.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <StickyNote className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No notes yet</h3>
            <p className="text-white/50">Write or record your first note above</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="trade-card rounded-sm p-5"
                data-testid={`note-card-${n.id}`}
              >
                {editingId === n.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      rows={4}
                      className="trade-input rounded-sm text-sm"
                      data-testid={`edit-note-textarea-${n.id}`}
                    />
                    <div>
                      <Label className="text-[11px] text-white/50">Reminder date/time</Label>
                      <Input
                        type="datetime-local"
                        value={editReminderAt}
                        onChange={e => setEditReminderAt(e.target.value)}
                        className="trade-input mt-1 h-10 rounded-sm text-sm"
                        data-testid={`edit-note-reminder-${n.id}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(n.id)}
                        className="flex-1 text-xs trade-green hover:bg-[#10b981]/10 py-2 rounded-sm flex items-center justify-center gap-1 border border-[#10b981]/30"
                        data-testid={`save-edit-note-${n.id}`}
                      >
                        <Check size={12} /> Save
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditText(""); setEditReminderAt(""); }}
                        className="text-xs text-white/60 hover:bg-white/5 px-3 py-2 rounded-sm border border-white/10"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {n.audio_path && (
                      <div className="mb-4 rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3" data-testid={`voice-note-audio-${n.id}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileAudio size={15} className="trade-gold flex-shrink-0" />
                            <span className="text-xs text-white/75 truncate">{n.audio_name || "Voice note"}</span>
                          </div>
                          {n.audio_transcript_language && (
                            <span className="text-[9px] uppercase tracking-widest text-white/35 flex-shrink-0">
                              {n.audio_transcript_language}
                            </span>
                          )}
                        </div>
                        <audio
                          controls
                          preload="none"
                          src={`${API}/trade/notes/${n.id}/audio?auth=${token}`}
                          className="w-full h-10 mb-3"
                          data-testid={`note-audio-player-${n.id}`}
                        />
                        <button
                          onClick={() => handleNoteTranscribe(n)}
                          disabled={transcribingId === n.id}
                          className="w-full text-xs trade-gold hover:bg-[#D4AF37]/10 py-2 rounded-sm flex items-center justify-center gap-1.5 border border-[#D4AF37]/30 transition-colors"
                          data-testid={`convert-note-${n.id}`}
                        >
                          <FileText size={12} />
                          {transcribingId === n.id ? "Writing..." : n.audio_transcript ? "Convert Again" : "Convert to Text"}
                        </button>
                      </div>
                    )}
                    {n.text ? (
                      <p className="text-sm text-white/85 whitespace-pre-wrap mb-4 min-h-[3rem]" data-testid={`note-text-${n.id}`}>{n.text}</p>
                    ) : (
                      <p className="text-sm text-white/40 italic mb-4 min-h-[3rem]" data-testid={`note-text-${n.id}`}>
                        Voice note saved — convert it to text when ready.
                      </p>
                    )}
                    {n.reminder_at && (
                      <div className={`mb-4 rounded-sm border p-3 ${
                        n.reminder_done
                          ? "border-white/10 bg-white/5"
                          : isReminderDue(n)
                          ? "border-[#D4AF37]/40 bg-[#D4AF37]/10"
                          : "border-white/10 bg-white/5"
                      }`} data-testid={`note-reminder-${n.id}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <AlarmClock size={14} className={n.reminder_done ? "text-white/35" : "trade-gold"} />
                            <div className="min-w-0">
                              <p className={`text-[10px] uppercase tracking-widest ${n.reminder_done ? "text-white/30" : isReminderDue(n) ? "trade-gold" : "text-white/40"}`}>
                                {n.reminder_done ? "Reminder done" : isReminderDue(n) ? "Reminder due" : "Reminder set"}
                              </p>
                              <p className="text-xs text-white/60 truncate">
                                {new Date(n.reminder_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {!n.reminder_done && (
                            n.bible_verse ? (
                              <button
                                onClick={() => markVerseRead(n.id)}
                                className="text-[11px] trade-gold border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 px-2.5 py-1.5 rounded-sm flex-shrink-0 flex items-center gap-1"
                                data-testid={`note-verse-read-${n.id}`}
                              >
                                <BookOpen size={11} /> Read — አንብቤያለሁ
                              </button>
                            ) : (
                              <button
                                onClick={() => markReminderDone(n.id)}
                                className="text-[11px] trade-green border border-[#10b981]/30 hover:bg-[#10b981]/10 px-2.5 py-1.5 rounded-sm flex-shrink-0"
                                data-testid={`note-reminder-done-${n.id}`}
                              >
                                Done
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-white/5">
                      <span className="text-[10px] text-white/40">
                        {new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => startEdit(n)}
                          className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                          data-testid={`edit-note-${n.id}`}
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="p-2 text-red-400/70 hover:text-red-400 transition-colors"
                          data-testid={`delete-note-${n.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPage;
